/**
 * Legal texts for A Gift of Memories (Incubedreams S.L.)
 * Adapted from the master template provided by the client,
 * removing wedding/health-data sections that don't apply.
 *
 * Spanish is the legally binding version (company in Spain).
 * Translations for EN/FR/DE are provided as reference.
 */

const COMPANY = {
  name: 'Incubedreams S.L.',
  nif: 'B19718907',
  address: 'C/ Luchana 27, 28010 Madrid, España',
  email: 'info@agiftofmemories.com',
  refundEmail: 'admin@agiftofmemories.com',
  phone: '+34 919 93 34 27',
  domain: 'https://agiftofmemories.com',
};

export const LEGAL_META = COMPANY;

const ES = {
  aviso_legal: {
    title: 'Aviso Legal',
    lastUpdated: 'Última actualización: abril de 2026',
    sections: [
      {
        heading: '1. Titular de la aplicación',
        paragraphs: [
          `En cumplimiento de lo dispuesto en la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico, se informa a los usuarios de los datos identificativos del titular de este sitio web.`,
          `El titular del presente sitio web es ${COMPANY.name}, con NIF ${COMPANY.nif}, domicilio en ${COMPANY.address}, teléfono de contacto ${COMPANY.phone}, correo electrónico ${COMPANY.email} y dominio ${COMPANY.domain}. En adelante, el «Titular».`,
        ],
      },
      {
        heading: '2. Objeto',
        paragraphs: [
          `El presente Aviso Legal regula el acceso, navegación y uso del sitio web y aplicación titularidad de ${COMPANY.name}, así como las responsabilidades derivadas de la utilización de sus contenidos, funcionalidades, servicios, textos, imágenes, diseños, software, bases de datos, interfaces y cualquier otro elemento disponible en la plataforma.`,
          `El acceso y uso de la aplicación atribuye la condición de usuario e implica la aceptación del presente Aviso Legal, sin perjuicio de que determinados servicios puedan estar sujetos a condiciones particulares adicionales.`,
        ],
      },
      {
        heading: '3. Usuarios',
        paragraphs: [
          `La aplicación está dirigida a usuarios particulares mayores de edad que desean crear un libro personalizado de recuerdos para un ser querido. Cuando el usuario actúe en nombre de un tercero (por ejemplo, para regalar el libro), declara contar con autorización suficiente para aceptar el presente Aviso Legal y utilizar la aplicación en su nombre.`,
        ],
      },
      {
        heading: '4. Uso correcto de la aplicación',
        paragraphs: [
          `El usuario se compromete a utilizar la aplicación de forma lícita, diligente, correcta y conforme a la normativa aplicable, la buena fe, el orden público y el presente Aviso Legal. Queda prohibido utilizar la aplicación para fines ilícitos, fraudulentos, lesivos de derechos de terceros o que puedan dañar, inutilizar, sobrecargar o deteriorar el funcionamiento de la plataforma.`,
          `El usuario no podrá introducir, almacenar o difundir contenidos ilícitos, ofensivos, difamatorios, discriminatorios, violentos o que vulneren derechos de terceros. El usuario no podrá acceder o intentar acceder a zonas restringidas sin autorización, suplantar la identidad de terceros, interferir técnicamente en la plataforma, enviar comunicaciones no autorizadas o introducir virus, malware o código dañino.`,
          `El usuario no podrá introducir datos personales de terceros (protagonista del libro, colaboradores, familiares) sin contar con legitimación suficiente para ello y, cuando resulte necesario, sin haberles informado previamente del tratamiento.`,
        ],
      },
      {
        heading: '5. Propiedad intelectual e industrial',
        paragraphs: [
          `Todos los contenidos de la aplicación, incluyendo textos, fotografías, imágenes, vídeos, diseños, logotipos, marcas, nombres comerciales, interfaces, estructura, bases de datos, software, código fuente, código objeto y demás elementos protegibles, son titularidad de ${COMPANY.name} o de terceros que han autorizado su uso.`,
          `El usuario no adquiere ningún derecho de propiedad intelectual o industrial por el mero uso de la aplicación. Queda prohibida la reproducción, distribución, comunicación pública, transformación, extracción, reutilización o cualquier otra forma de explotación de los contenidos sin autorización expresa y por escrito del Titular, salvo en los casos legalmente permitidos.`,
          `El libro final creado por el usuario y los recuerdos aportados por los colaboradores son propiedad del usuario que encarga y paga el libro, sin perjuicio de la licencia limitada que se concede al Titular para prestar el servicio (ver Términos y Condiciones).`,
        ],
      },
      {
        heading: '6. Responsabilidad',
        paragraphs: [
          `${COMPANY.name} realizará sus mejores esfuerzos para que la aplicación funcione correctamente y se encuentre disponible, aunque no garantiza la inexistencia de interrupciones, errores técnicos, fallos de seguridad, pérdidas de información o incompatibilidades derivadas del uso de la plataforma.`,
          `El Titular no será responsable del uso indebido de la aplicación por parte de los usuarios, de los contenidos, datos o documentos introducidos por usuarios o terceros, ni de la falta de legitimación del usuario para introducir datos personales de terceros.`,
          `El Titular no será responsable de daños derivados de errores, interrupciones o fallos técnicos no imputables directamente a ${COMPANY.name}, ni de contenidos, servicios o enlaces de terceros accesibles desde la aplicación.`,
          `El Titular no garantiza la veracidad, exactitud o actualización de los datos facilitados por los usuarios o colaboradores.`,
        ],
      },
      {
        heading: '7. Protección de datos y cookies',
        paragraphs: [
          `El tratamiento de los datos personales de los usuarios se regula conforme a lo dispuesto en la Política de Privacidad. La aplicación utiliza cookies y tecnologías similares de acuerdo con lo indicado en la Política de Cookies.`,
        ],
      },
      {
        heading: '8. Legislación aplicable y jurisdicción',
        paragraphs: [
          `El presente Aviso Legal se regirá por la legislación española. Para cualquier controversia derivada del acceso o uso de la aplicación, las partes se someterán a los juzgados y tribunales que resulten competentes conforme a la normativa aplicable.`,
        ],
      },
    ],
  },

  privacidad: {
    title: 'Política de Privacidad',
    lastUpdated: 'Última actualización: abril de 2026',
    sections: [
      {
        heading: '1. Responsable del tratamiento',
        paragraphs: [
          `El responsable del tratamiento de los datos personales es ${COMPANY.name}, con NIF ${COMPANY.nif}, domicilio en ${COMPANY.address}, teléfono ${COMPANY.phone} y correo electrónico de contacto ${COMPANY.email}.`,
        ],
      },
      {
        heading: '2. Usuarios a los que se dirige la aplicación',
        paragraphs: [
          `La aplicación se dirige a:`,
        ],
        list: [
          'Usuarios registrados (clientes) que contratan la creación de un libro personalizado.',
          'Protagonistas del libro (familiares o allegados del cliente) cuyos datos son introducidos por el propio cliente.',
          'Colaboradores invitados a aportar recuerdos, textos o fotografías para la elaboración del libro.',
        ],
      },
      {
        heading: '3. Categorías de datos personales tratados',
        paragraphs: [`Se tratan las siguientes categorías de datos personales:`],
        list: [
          'Datos identificativos: nombre, apellidos, fecha de nacimiento.',
          'Datos de contacto: correo electrónico, dirección postal (para envío del libro).',
          'Datos de cuenta: credenciales de acceso, preferencias, idioma, historial de actividad.',
          'Datos de contratación: libro contratado, cupones aplicados, estado del pedido.',
          'Datos de facturación: datos fiscales, dirección de facturación, importes, impuestos, facturas.',
          'Datos técnicos: dirección IP, logs de acceso, navegador, sistema operativo, identificadores técnicos.',
          'Contenidos aportados por el usuario: fotografías del protagonista, anécdotas, recuerdos, textos y demás información que aporte el cliente o los colaboradores sobre el protagonista.',
          'Datos derivados del uso de inteligencia artificial: prompts, respuestas, textos generados por IA durante la elaboración del libro o en la asistencia vía chatbot.',
        ],
      },
      {
        heading: '4. Finalidades del tratamiento',
        paragraphs: [`Los datos se tratarán para las siguientes finalidades:`],
        list: [
          'Permitir el registro, acceso y uso de la aplicación.',
          'Gestionar la relación contractual con los clientes (creación del libro, cobros, envío).',
          'Recopilar los recuerdos aportados por los colaboradores mediante formularios específicos.',
          'Generar el libro mediante funcionalidades de inteligencia artificial.',
          'Gestionar la impresión, maquetación y envío físico del libro.',
          'Emitir facturas y cumplir obligaciones contables y fiscales.',
          'Atender consultas, soporte técnico e incidencias a través del formulario de contacto o del asistente IA.',
          'Enviar comunicaciones operativas relacionadas con el servicio (invitaciones, recordatorios, confirmaciones).',
          'Enviar, en su caso, comunicaciones comerciales sobre servicios similares cuando exista base legítima o consentimiento.',
          'Aplicar descuentos, cupones y promociones.',
          'Garantizar la seguridad, prevenir abusos, fraudes y accesos no autorizados.',
          'Analizar el uso de la aplicación con fines técnicos y estadísticos.',
          'Cumplir con las obligaciones legales aplicables.',
        ],
      },
      {
        heading: '5. Base jurídica del tratamiento',
        list: [
          'Ejecución del contrato: para la creación del libro, pagos, facturación, impresión, envío y soporte.',
          'Consentimiento del usuario: para comunicaciones comerciales (cuando sea necesario) y funcionalidades opcionales.',
          'Interés legítimo: para garantizar la seguridad de la plataforma, prevenir fraudes, mejorar el servicio y responder a consultas.',
          'Cumplimiento de obligaciones legales: para atender obligaciones fiscales, contables, mercantiles o requerimientos de autoridades competentes.',
        ],
      },
      {
        heading: '6. Datos de terceros introducidos por el cliente',
        paragraphs: [
          `Cuando el cliente introduzca en la aplicación datos personales de terceros (protagonista del libro, colaboradores, familiares), declara contar con legitimación suficiente para hacerlo y haber informado previamente a los afectados cuando resulte obligatorio.`,
          `El cliente se compromete especialmente a:`,
        ],
        list: [
          'No introducir datos personales de menores sin el consentimiento de sus tutores legales.',
          'No introducir datos especialmente protegidos (salud, origen racial, creencias, etc.) sin base jurídica válida.',
          'Informar a los colaboradores del tratamiento de sus datos al invitarles mediante email.',
          'Respetar los derechos de las personas cuyos datos se han incluido en el libro.',
        ],
      },
      {
        heading: '7. Inteligencia artificial',
        paragraphs: [
          `La aplicación utiliza funcionalidades de inteligencia artificial para:`,
        ],
        list: [
          'Generación del texto del libro a partir de los recuerdos aportados (proveedor: Anthropic — modelo Claude Sonnet 4.5, con servidores en EE. UU., accedido a través del servicio Emergent Integrations).',
          'Generación de ilustraciones y portadas (proveedor: OpenAI — modelo GPT Image 1, con servidores en EE. UU.).',
          'Asistente conversacional de ayuda en la sección de soporte (proveedor: Anthropic — Claude Sonnet 4.5).',
        ],
        afterList: [
          `Los datos introducidos en estas funcionalidades se utilizan exclusivamente para prestar el servicio y no se emplean para entrenar modelos de IA generales. El usuario deberá revisar y validar los contenidos generados por IA antes de su aprobación final. Los resultados generados por IA pueden contener errores, imprecisiones u omisiones.`,
        ],
      },
      {
        heading: '8. Conservación de los datos',
        list: [
          'Los datos de cuenta se conservarán mientras el usuario mantenga la cuenta activa.',
          'Los datos contractuales y de facturación se conservarán durante los plazos legalmente exigibles (hasta 6 años por obligaciones mercantiles y fiscales).',
          'Las fotografías y recuerdos aportados se conservarán hasta la entrega del libro físico y, posteriormente, hasta que el cliente solicite su supresión, salvo obligación legal.',
          'Los datos técnicos y logs de seguridad se conservarán durante el plazo necesario para garantizar la trazabilidad y seguridad (máximo 12 meses).',
          'Tras los plazos indicados, los datos podrán conservarse bloqueados durante los plazos de prescripción aplicables.',
        ],
      },
      {
        heading: '9. Destinatarios de los datos',
        paragraphs: [`Los datos podrán comunicarse a:`],
        list: [
          'Administraciones públicas, juzgados, tribunales o autoridades competentes cuando exista obligación legal.',
          'Entidad de pago Monei Payments S.L. (España) para la gestión de cobros con tarjeta.',
          'Proveedor de infraestructura y alojamiento (Emergent Integrations LLC, EE. UU. / UE).',
          'Proveedores de servicios de IA: Anthropic (Claude) y OpenAI (imágenes), ambos con sede en EE. UU.',
          'Proveedor de CDN y resolución DNS: Cloudflare Inc. (EE. UU./UE).',
          'Proveedor de correo electrónico transaccional (servidor SMTP propio de Pradosmoros alojado en España).',
          'Imprenta y empresa de mensajería colaboradora para la impresión y envío físico del libro.',
          'Asesores legales, fiscales, contables o auditores, cuando sea necesario.',
        ],
      },
      {
        heading: '10. Transferencias internacionales de datos',
        paragraphs: [
          `Algunos proveedores tecnológicos están ubicados fuera del Espacio Económico Europeo, concretamente:`,
        ],
        list: [
          'Anthropic, PBC (EE. UU.) — proveedor de los modelos de IA Claude.',
          'OpenAI, LLC (EE. UU.) — proveedor de generación de imágenes.',
          'Cloudflare, Inc. (EE. UU./UE) — CDN y DNS.',
          'Emergent Integrations LLC (EE. UU./UE) — infraestructura.',
        ],
        afterList: [
          `Cuando se producen transferencias internacionales, ${COMPANY.name} adopta las garantías legalmente exigidas mediante Cláusulas Contractuales Tipo aprobadas por la Comisión Europea u otros mecanismos válidos conforme al RGPD.`,
        ],
      },
      {
        heading: '11. Derechos de los usuarios',
        paragraphs: [`El usuario podrá ejercer los siguientes derechos:`],
        list: [
          'Acceso a sus datos personales.',
          'Rectificación de datos inexactos o incompletos.',
          'Supresión de sus datos cuando proceda.',
          'Oposición al tratamiento.',
          'Limitación del tratamiento.',
          'Portabilidad de los datos.',
          'Retirada del consentimiento en cualquier momento.',
          'Derecho a no ser objeto de decisiones automatizadas con efectos jurídicos.',
        ],
      },
      {
        heading: '12. Ejercicio de derechos',
        paragraphs: [
          `Para ejercer estos derechos, el usuario podrá enviar una solicitud a ${COMPANY.email}, indicando claramente el derecho que desea ejercer y adjuntando copia de un documento identificativo cuando sea necesario para verificar la identidad.`,
          `El usuario también tiene derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es) si considera que el tratamiento de sus datos no se ajusta a la normativa aplicable.`,
        ],
      },
      {
        heading: '13. Seguridad',
        paragraphs: [
          `${COMPANY.name} aplica medidas técnicas y organizativas adecuadas para proteger los datos personales frente a accesos no autorizados, pérdida, alteración o divulgación: cifrado en tránsito (HTTPS/TLS), controles de acceso, registros de actividad, copias de seguridad, políticas de contraseñas seguras y minimización de datos.`,
        ],
      },
    ],
  },

  cookies: {
    title: 'Política de Cookies',
    lastUpdated: 'Última actualización: abril de 2026',
    sections: [
      {
        heading: '1. Responsable',
        paragraphs: [
          `El responsable de la presente Política de Cookies es ${COMPANY.name}, con NIF ${COMPANY.nif}, domicilio en ${COMPANY.address}, y correo electrónico ${COMPANY.email}.`,
        ],
      },
      {
        heading: '2. ¿Qué son las cookies?',
        paragraphs: [
          `Las cookies son pequeños archivos que se almacenan en el dispositivo del usuario cuando accede a una página web o aplicación. También pueden utilizarse tecnologías similares como Local Storage o Session Storage. Estas tecnologías permiten, entre otras cosas, mantener sesiones activas, recordar preferencias, mejorar la seguridad o conservar información necesaria para el funcionamiento del servicio.`,
        ],
      },
      {
        heading: '3. Cookies y tecnologías utilizadas',
        paragraphs: [
          `La aplicación utiliza actualmente las siguientes cookies y tecnologías similares:`,
        ],
        table: {
          headers: ['Cookie / tecnología', 'Titular', 'Tipo', 'Finalidad', 'Duración'],
          rows: [
            ['session_token', 'Incubedreams S.L.', 'Técnica (HttpOnly, Secure)', 'Mantener la sesión del usuario autenticado en el panel privado', '7 días (30 si marca «Recuérdame»)'],
            ['i18n_lang (Local Storage)', 'Incubedreams S.L.', 'Preferencia', 'Recordar el idioma elegido por el usuario', 'Permanente hasta que el usuario lo borre'],
            ['help_chat_session (Session Storage)', 'Incubedreams S.L.', 'Técnica', 'Mantener el contexto de la conversación con el asistente IA', 'Hasta cerrar la pestaña del navegador'],
          ],
        },
      },
      {
        heading: '4. Cookies técnicas',
        paragraphs: [
          `Las cookies técnicas son necesarias para que la aplicación funcione correctamente y para prestar los servicios solicitados por el usuario. Se utilizan para mantener la sesión del usuario, garantizar el acceso seguro a áreas privadas, conservar el contexto del chat de asistencia y prestar funcionalidades básicas de la plataforma. Estas cookies no requieren consentimiento del usuario conforme a la normativa vigente al ser estrictamente necesarias para prestar el servicio solicitado.`,
        ],
      },
      {
        heading: '5. Cookies de preferencia',
        paragraphs: [
          `La aplicación utiliza una tecnología de almacenamiento local (i18n_lang) para recordar el idioma elegido por el usuario. Esta preferencia es seleccionada directamente por el usuario y se considera necesaria para prestar una funcionalidad solicitada por él.`,
        ],
      },
      {
        heading: '6. Cookies de análisis, publicidad o terceros',
        paragraphs: [
          `Actualmente, la aplicación no utiliza cookies de análisis, publicidad comportamental ni cookies de terceros. Si en el futuro ${COMPANY.name} incorporase herramientas como Google Analytics, Meta Pixel, Hotjar u otros servicios que instalen cookies o tecnologías similares, esta Política de Cookies se actualizará y se solicitará el consentimiento previo del usuario cuando sea necesario.`,
        ],
      },
      {
        heading: '7. Gestión de cookies',
        paragraphs: [
          `El usuario puede configurar su navegador para permitir, bloquear o eliminar cookies y tecnologías similares. La desactivación de las cookies técnicas puede impedir el correcto funcionamiento de la aplicación, especialmente el inicio de sesión y la navegación por áreas privadas.`,
          `Enlaces de configuración según navegador: Chrome, Firefox, Safari, Edge.`,
        ],
      },
    ],
  },

  terminos: {
    title: 'Términos y Condiciones',
    lastUpdated: 'Última actualización: abril de 2026',
    sections: [
      {
        heading: '1. Titular',
        paragraphs: [
          `Los presentes Términos y Condiciones regulan el acceso, registro, uso y contratación de los servicios ofrecidos a través del sitio web y aplicación titularidad de ${COMPANY.name}, con NIF ${COMPANY.nif}, domicilio en ${COMPANY.address}, teléfono ${COMPANY.phone} y correo electrónico ${COMPANY.email}.`,
        ],
      },
      {
        heading: '2. Aceptación',
        paragraphs: [
          `El acceso, registro, contratación o uso de la aplicación implica la aceptación expresa y sin reservas de los presentes Términos y Condiciones. Cuando el usuario actúe en nombre de un tercero, declara contar con autorización suficiente para aceptar estos términos en su nombre.`,
        ],
      },
      {
        heading: '3. Descripción del servicio',
        paragraphs: [
          `${COMPANY.name} pone a disposición de los usuarios una aplicación digital que permite la creación de un libro personalizado de recuerdos de un ser querido, mediante la recopilación de anécdotas, textos y fotografías aportados por familiares, amigos o allegados (colaboradores) invitados por el cliente. El contenido se procesa mediante funcionalidades de inteligencia artificial para generar un libro coherente y estructurado que, finalmente, se imprime y se envía físicamente al cliente.`,
          `Las características concretas del servicio, precios y condiciones particulares se indicarán en la propia aplicación en el momento de la contratación.`,
        ],
      },
      {
        heading: '4. Modalidad de contratación',
        paragraphs: [
          `La contratación se realiza mediante pago único por cada libro que el cliente desee crear. No se aplican suscripciones periódicas ni renovaciones automáticas.`,
          `El precio base del libro y los ejemplares adicionales se muestran claramente en el proceso de contratación, con desglose de impuestos aplicables. El cliente podrá adquirir ejemplares adicionales al precio vigente en el momento de la solicitud.`,
        ],
      },
      {
        heading: '5. Cupones y promociones',
        paragraphs: [
          `${COMPANY.name} podrá ofrecer cupones, descuentos y promociones sujetos a las condiciones indicadas en cada caso (duración, ámbito de aplicación, compatibilidad, limitaciones, caducidad). Salvo que se indique lo contrario, los cupones no son canjeables por dinero ni acumulables entre sí.`,
        ],
      },
      {
        heading: '6. Pagos y facturación',
        paragraphs: [
          `Los pagos se realizan mediante la pasarela de pago Monei Payments S.L. (España), que admite tarjetas de crédito y débito habituales (Visa, Mastercard, American Express). El usuario garantiza estar autorizado para utilizar el medio de pago seleccionado.`,
          `En caso de impago, devolución del cargo o uso fraudulento del medio de pago, ${COMPANY.name} podrá suspender el servicio y reclamar las cantidades pendientes. El usuario recibirá la factura correspondiente por correo electrónico y podrá descargarla desde su panel privado.`,
        ],
      },
      {
        heading: '7. Proceso de creación del libro',
        paragraphs: [
          `El proceso de creación del libro consta, a efectos orientativos, de las siguientes fases:`,
        ],
        list: [
          'Alta del libro por parte del cliente con los datos del protagonista y pago inicial.',
          'Invitación por parte del cliente a los colaboradores mediante email, para que aporten sus recuerdos.',
          'Recopilación de contribuciones durante el plazo establecido (ampliable por el cliente).',
          'Generación del texto y la maquetación mediante inteligencia artificial.',
          'Revisión, aprobación y solicitud de cambios por parte del cliente antes de imprimir.',
          'Impresión física y envío del libro a la dirección indicada por el cliente.',
        ],
      },
      {
        heading: '8. Envíos y entrega',
        paragraphs: [
          `Una vez que el cliente aprueba el PDF final del libro, ${COMPANY.name} procede a la impresión física y al envío postal del ejemplar (o ejemplares) contratado.`,
          `El envío se realiza a través de operadores logísticos colaboradores —entre ellos Correos, Correos Express, SEUR, MRW y GLS— seleccionados en cada caso según el destino, el volumen del pedido y los tiempos disponibles. El operador concreto que efectuará la entrega se indicará al cliente en el correo electrónico de confirmación del envío, junto con el número de seguimiento cuando proceda.`,
          `El plazo estimado de entrega es de 10 a 15 días laborables desde la aprobación del PDF final por parte del cliente, sumando el tiempo de impresión y el de transporte. Los plazos son orientativos y pueden variar por causas ajenas a ${COMPANY.name} (incidencias del transportista, direcciones incompletas, periodos de alta demanda, festivos locales, catástrofes, huelgas o fuerza mayor). Para envíos fuera de España peninsular (Baleares, Canarias, Ceuta, Melilla o internacionales) los plazos pueden ampliarse.`,
          `Los gastos de envío son variables en función del destino de entrega y del número de ejemplares del pedido. El importe exacto se calcula y se muestra al cliente en el resumen del carrito antes de confirmar el pago, por lo que el cliente conoce de antemano el coste total del pedido (libro + envío + impuestos aplicables).`,
          `Si el cliente encarga ejemplares adicionales o un segundo libro, los gastos de envío se recalcularán en función del volumen y destino de cada pedido en el momento de la solicitud.`,
          `Es responsabilidad del cliente facilitar una dirección de entrega correcta y completa. En caso de devolución del pedido al remitente por dirección errónea, ausencia del destinatario reiterada o rechazo del envío sin motivo justificado, el cliente asumirá el coste del nuevo envío si desea recibir el libro.`,
        ],
      },
      {
        heading: '9. Cancelaciones, devoluciones y reembolsos',
        paragraphs: [
          `El libro objeto del contrato es un producto personalizado creado a medida según las aportaciones específicas del cliente y los colaboradores. De conformidad con el artículo 103.c) del Real Decreto Legislativo 1/2007, de 16 de noviembre, General para la Defensa de los Consumidores y Usuarios, el derecho de desistimiento no es aplicable a bienes confeccionados conforme a las especificaciones del consumidor o claramente personalizados.`,
          `No obstante, ${COMPANY.name} ofrece la siguiente política de cancelaciones y reembolsos:`,
        ],
        list: [
          `Antes de iniciar la generación con IA: el cliente puede cancelar el pedido hasta 14 días naturales después del pago, solicitándolo por correo electrónico a ${COMPANY.refundEmail}. Se reembolsará el 100 % del importe abonado en la misma forma de pago utilizada, en un plazo máximo de 14 días naturales desde la confirmación de la cancelación.`,
          `Durante la fase de generación con IA: la cancelación es posible, pero conllevará la retención de los costes operativos ya incurridos (procesamiento con IA, tiempo de dedicación editorial). ${COMPANY.name} informará al cliente del importe retenido antes de formalizar la cancelación; el resto se reembolsará en un plazo de 14 días.`,
          `Tras la aprobación del PDF final por el cliente y una vez iniciada la impresión o el envío: al tratarse de un producto totalmente personalizado, NO procede reembolso salvo en los casos contemplados a continuación.`,
          `Defecto de producción o daño durante el transporte: si el libro llega dañado, defectuoso o con errores atribuibles a la impresión o al transporte, el cliente debe comunicarlo en un plazo de 14 días naturales desde la recepción a ${COMPANY.refundEmail} adjuntando fotografías que acrediten el defecto. ${COMPANY.name} sustituirá el ejemplar sin coste o, si no es posible, reembolsará el importe correspondiente.`,
          `Cambios tras la aprobación del PDF final: cualquier modificación solicitada tras la aprobación del PDF final podrá conllevar un coste adicional que se comunicará al cliente antes de ejecutar el cambio.`,
        ],
        afterList: [
          `Procedimiento para solicitar la cancelación o reembolso: el cliente debe enviar un correo electrónico a ${COMPANY.refundEmail} indicando el identificador del pedido, el motivo de la solicitud y los datos del medio de pago si así se requiere. ${COMPANY.name} responderá en un plazo máximo de 5 días laborables confirmando la aceptación y los plazos de reembolso.`,
          `Los reembolsos se realizarán siempre en la misma forma de pago utilizada por el cliente (tarjeta, cargo bancario), salvo acuerdo expreso en contrario.`,
        ],
      },
      {
        heading: '10. Obligaciones del usuario',
        list: [
          'Utilizar la aplicación conforme a la ley y a estos términos.',
          'Facilitar datos veraces y actualizados en el formulario del cliente y en los formularios para colaboradores.',
          'Custodiar sus credenciales de acceso y notificar cualquier uso no autorizado.',
          'No introducir contenidos ilícitos, ofensivos o que vulneren derechos de terceros.',
          'No introducir datos personales de terceros sin legitimación suficiente (especialmente datos de menores).',
          'Revisar y aprobar el PDF final antes de la impresión.',
          'No copiar, modificar, descompilar, realizar ingeniería inversa ni explotar técnicamente la aplicación.',
          'No introducir virus, malware ni realizar scraping no autorizado.',
        ],
      },
      {
        heading: '11. Datos de colaboradores y terceros',
        paragraphs: [
          `El cliente es responsable de contar con legitimación suficiente para invitar a colaboradores e introducir datos de terceros (protagonista, familiares). Al invitar a un colaborador, el cliente deberá informarle sobre el tratamiento de sus datos. El colaborador, al aportar sus contribuciones, acepta la Política de Privacidad.`,
        ],
      },
      {
        heading: '12. Contenidos aportados por el usuario',
        paragraphs: [
          `El usuario conserva la titularidad de los contenidos que incorpore a la plataforma (textos, fotografías, anécdotas). Al subir contenidos en la aplicación, el usuario concede a ${COMPANY.name} una licencia no exclusiva, limitada, gratuita y necesaria para alojar, reproducir, procesar mediante IA, transformar técnicamente, maquetar, imprimir y enviar dichos contenidos, únicamente con la finalidad de prestar el servicio contratado.`,
          `El usuario garantiza que dispone de todos los derechos, permisos y autorizaciones necesarios sobre los contenidos que suba a la plataforma (incluidos los derechos de imagen de las personas aparecidas en las fotografías).`,
        ],
      },
      {
        heading: '13. Inteligencia artificial',
        paragraphs: [
          `La aplicación incorpora funcionalidades basadas en inteligencia artificial (Anthropic Claude y OpenAI GPT Image 1) para la generación del texto y las ilustraciones del libro, así como para el asistente conversacional de ayuda.`,
          `El usuario reconoce que los resultados generados por IA pueden contener errores, imprecisiones u omisiones. El usuario deberá revisar y validar cualquier contenido generado mediante IA antes de aprobarlo para su impresión. ${COMPANY.name} no garantiza que los contenidos generados automáticamente sean exactos, completos o libres de errores, y el usuario es el responsable final de la revisión y aprobación del libro.`,
        ],
      },
      {
        heading: '14. Propiedad intelectual de la plataforma',
        paragraphs: [
          `La aplicación, su software, diseño, arquitectura, bases de datos, interfaces, funcionalidades, marcas, logotipos y documentación son titularidad de ${COMPANY.name} o de sus licenciantes. El usuario recibe únicamente un derecho de uso limitado, no exclusivo, no transferible y revocable para acceder y utilizar la plataforma conforme a estos términos.`,
        ],
      },
      {
        heading: '15. Disponibilidad del servicio',
        paragraphs: [
          `${COMPANY.name} intentará mantener la aplicación disponible de forma continuada, pero no garantiza una disponibilidad ininterrumpida. El servicio podrá verse afectado por mantenimientos programados, actualizaciones, incidencias técnicas, fallos de terceros o causas de fuerza mayor.`,
        ],
      },
      {
        heading: '16. Suspensión o cancelación del servicio',
        paragraphs: [
          `${COMPANY.name} podrá suspender o cancelar el acceso del usuario cuando: incumpla estos términos, existan impagos, se detecten usos fraudulentos o ilícitos, se comprometa la seguridad de la plataforma, sea requerido por una autoridad competente o el usuario introduzca datos personales sin legitimación.`,
        ],
      },
      {
        heading: '17. Limitación de responsabilidad',
        paragraphs: [`${COMPANY.name} no será responsable de:`],
        list: [
          'Daños derivados del uso indebido de la aplicación.',
          'Errores causados por información incorrecta facilitada por el usuario o los colaboradores.',
          'La falta de autorización del usuario para introducir datos personales de terceros.',
          'La inexactitud o veracidad de los recuerdos aportados por los colaboradores.',
          'Decisiones adoptadas por usuarios o terceros a partir de información introducida en la plataforma.',
          'Resultados generados por IA que no hayan sido revisados y aprobados por el usuario antes de la impresión.',
          'Interrupciones, errores o fallos técnicos ajenos al control razonable de la empresa.',
          'Retrasos en la entrega derivados de la empresa de mensajería o imprenta colaboradora.',
        ],
      },
      {
        heading: '18. Protección de datos',
        paragraphs: [
          `El tratamiento de datos personales se regula conforme a la Política de Privacidad.`,
        ],
      },
      {
        heading: '19. Legislación aplicable y jurisdicción',
        paragraphs: [
          `Los presentes Términos y Condiciones se regirán por la legislación española. Cuando el usuario tenga la condición de consumidor, las controversias se someterán a los juzgados y tribunales que correspondan conforme a la normativa de consumidores y usuarios. Cuando el usuario actúe como empresario o profesional, las partes se someten, salvo norma imperativa en contrario, a los juzgados y tribunales de Madrid.`,
          `El usuario consumidor podrá acudir a la plataforma europea de resolución de litigios en línea: https://ec.europa.eu/consumers/odr/`,
        ],
      },
    ],
  },
};

/**
 * The legally binding version is Spanish.
 * For EN/FR/DE we reuse the Spanish content as the operational language
 * and include a notice that the legally binding version is in Spanish.
 *
 * For simplicity of deployment, we expose the Spanish texts for all locales
 * and add a language notice header on non-ES locales.
 */
export const LEGAL_DOCS = {
  es: ES,
  en: ES, // fallback to ES with notice
  fr: ES, // fallback to ES with notice
  de: ES, // fallback to ES with notice
};

export const NON_BINDING_NOTICE = {
  es: null,
  en: 'The legally binding version of these texts is available in Spanish. This page displays the Spanish version as a reference.',
  fr: 'La version juridiquement contraignante de ces textes est disponible en espagnol. Cette page affiche la version espagnole à titre de référence.',
  de: 'Die rechtsverbindliche Version dieser Texte ist in spanischer Sprache verfügbar. Diese Seite zeigt die spanische Version zu Referenzzwecken.',
};
